import { Link, Text } from '@loomhq/lens';
import cx from 'classnames';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import urlRegexSafe from 'url-regex-safe';

import { requestVideoPlayerCurrentTime } from 'actions/videoPlayer';
import { VIDEO_TIMESTAMP_CLASS } from 'constants/comments';
import { SHARE_VIDEO_DESCRIPTION_TIMESTAMP_CLICKED } from 'constants/events';
import { selectVideoDuration } from 'selectors/shareVideo';
import * as analytics from 'utilities/analytics';
import { playVideoAtTimestamp } from 'utilities/comments';
import { splitTrailingPunctuation } from 'utilities/url';

import { detectTimestamps } from './timestamps';

import styles from './styles.module.less';

const createLink = url => {
  const [trimmedUrl, trailing] = splitTrailingPunctuation(url);
  const hrefVal = /^www/.test(trimmedUrl) ? `//${trimmedUrl}` : trimmedUrl;

  return (
    <>
      <Link
        target="_blank"
        onClick={e => e.stopPropagation()}
        variant="neutral"
        href={hrefVal}
      >
        {trimmedUrl}
      </Link>
      {trailing}
    </>
  );
};

export const replaceURLs = (text, ind, prefix) => {
  const regResult = urlRegexSafe({ strict: true }).exec(text);

  if (regResult == null) {
    return <span key={`${ind}-${prefix}`}>{text}</span>;
  }

  const matchedUrl = regResult[0];

  return (
    <div className={styles.contents} key={`link-${ind}`}>
      <span>{text.substring(0, regResult.index)}</span>
      {createLink(matchedUrl)}
      {replaceURLs(
        text.substring(regResult.index + matchedUrl.length),
        ind + regResult.index,
        prefix
      )}
    </div>
  );
};

export const isEmpty = description =>
  description == null || description.length === 0;

function linkify(timestamps, description, dispatch) {
  const linkedDescription = [];
  let indexPointer = 0;

  timestamps.forEach(part => {
    // Add previous section
    linkedDescription.push(
      replaceURLs(
        description.substring(
          indexPointer,
          part.endIndex - part.literal.length
        ),
        indexPointer,
        'pre'
      )
    );

    const handleClickTimestamp = event => {
      analytics.track(SHARE_VIDEO_DESCRIPTION_TIMESTAMP_CLICKED);
      playVideoAtTimestamp.call(event.target, event);
      dispatch(requestVideoPlayerCurrentTime(part.time));

      window.scroll(0, 0);
    };

    // Add link
    linkedDescription.push(
      <Link
        key={indexPointer}
        variant="neutral"
        href="#"
        className={cx(VIDEO_TIMESTAMP_CLASS, styles.tabular)}
        onClick={handleClickTimestamp}
        data-seconds={part.time}
      >
        {part.literal}
      </Link>
    );

    indexPointer = part.endIndex;
  });

  // Add final part
  linkedDescription.push(
    replaceURLs(description.substring(indexPointer), indexPointer, 'post')
  );

  return linkedDescription;
}

const Description = ({ description, defaultText, onClick, canEdit }) => {
  const dispatch = useDispatch();
  const noDescription = isEmpty(description);

  const videoDuration = useSelector(selectVideoDuration);

  const timestamps = detectTimestamps(description, videoDuration);

  const linkedDescription = !noDescription
    ? linkify(timestamps, description, dispatch)
    : replaceURLs(description, 0, '');

  const classes = cx(styles.whiteSpaceBreak, styles.show, {
    [styles.descriptionHover]: canEdit,
    [styles.textCursor]: canEdit,
    [styles.hintPadding]: canEdit,
    [styles.matchPlaceholderColor]: noDescription && canEdit,
  });

  const text = canEdit ? (
    <Text size="medium" onClick={onClick} className={cx(classes)}>
      <>{noDescription && canEdit ? defaultText : linkedDescription}</>
    </Text>
  ) : (
    <Text size="medium" className={cx(classes)}>
      <>{linkedDescription}</>
    </Text>
  );

  return text;
};

export default Description;
